import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search, Loader2, Plus, Minus, ShoppingCart, Trash2, Download, Save, X, Truck, CheckCircle, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartsLookup({ onBack }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [basket, setBasket] = useState([]);
  const [projectName, setProjectName] = useState("Parts Quote");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState("");
  // Delivery state
  const [wantsDelivery, setWantsDelivery] = useState(false);
  const [showPostcodeModal, setShowPostcodeModal] = useState(false);
  const [postcode, setPostcode] = useState("");
  const [sendingDelivery, setSendingDelivery] = useState(false);
  const [deliverySuccess, setDeliverySuccess] = useState("");
  // Basket minimise state
  const [basketMinimised, setBasketMinimised] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const search = useCallback(async (q) => {
    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.get(`${API}/parts/search`, {
        params: { q },
        headers: getAuthHeaders(),
      });
      const data = res.data;
      setResults(data.results || []);
      setTotal(data.total || 0);
      if (data.results && data.results.length > 0) {
        setVisibleColumns(Object.keys(data.results[0]));
      }
    } catch (err) {
      console.error("Parts search failed:", err);
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    search("");
  }, [search]);

  const handleSearch = (e) => {
    e.preventDefault();
    search(query);
  };

  const formatPrice = (val) => {
    if (val === undefined || val === null) return "";
    return `£${Number(val).toFixed(2)}`;
  };

  const columnLabel = (key) => {
    const labels = {
      sku: "Product SKU",
      trade_installer_price: "Trade / Installer Price",
      distributor_price: "Distributor Price",
      rrp: "RRP",
    };
    return labels[key] || key;
  };

  // Basket functions
  const addToBasket = (item) => {
    setBasket((prev) => {
      const existing = prev.find((b) => b.sku === item.sku);
      if (existing) {
        return prev.map((b) =>
          b.sku === item.sku ? { ...b, quantity: b.quantity + 1 } : b
        );
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const updateQuantity = (sku, qty) => {
    if (qty < 1) {
      removeFromBasket(sku);
      return;
    }
    setBasket((prev) =>
      prev.map((b) => (b.sku === sku ? { ...b, quantity: qty } : b))
    );
  };

  const removeFromBasket = (sku) => {
    setBasket((prev) => prev.filter((b) => b.sku !== sku));
  };

  // Get the best price for the user's role from basket item
  const getItemPrice = (item) => {
    if (item.distributor_price !== undefined) return item.distributor_price;
    if (item.trade_installer_price !== undefined) return item.trade_installer_price;
    return item.rrp;
  };

  const basketSubtotal = basket.reduce(
    (sum, item) => sum + getItemPrice(item) * item.quantity,
    0
  );

  // Small order fee logic - always based on distributor cost
  const getSmallOrderFee = () => {
    // Calculate distributor total regardless of user's role
    const distTotal = basket.reduce((sum, item) => {
      // Use distributor_price if available, otherwise fall back to the lowest price
      const distPrice = item.distributor_price !== undefined
        ? item.distributor_price
        : (item.trade_installer_price !== undefined ? item.trade_installer_price : item.rrp);
      return sum + distPrice * item.quantity;
    }, 0);
    if (distTotal >= 500) return 0;
    // Distributor-tier users pay £25, installers pay £30
    if (basket.length > 0 && basket[0].distributor_price !== undefined) return 25;
    if (basket.length > 0 && basket[0].trade_installer_price !== undefined) return 30;
    return 0;
  };

  // Determine if user is distributor-tier (can see the £500 threshold)
  const isDistributorTier = basket.length > 0 && basket[0].distributor_price !== undefined;

  const smallOrderFee = getSmallOrderFee();
  const basketTotal = basketSubtotal + smallOrderFee;

  const handleSaveQuote = async () => {
    if (basket.length === 0) return;
    setSaving(true);
    setSaveSuccess("");
    try {
      const payload = {
        project_name: projectName,
        items: basket.map((b) => ({ sku: b.sku, quantity: b.quantity })),
      };
      const res = await axios.post(`${API}/parts/quote/save`, payload, {
        headers: getAuthHeaders(),
      });
      setSaveSuccess(`Quote saved: ${res.data.quote_reference}`);
    } catch (err) {
      console.error("Save failed:", err);
      setSaveSuccess("Error saving quote");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (basket.length === 0) return;
    setDownloading(true);
    try {
      const payload = {
        project_name: projectName,
        items: basket.map((b) => ({ sku: b.sku, quantity: b.quantity })),
      };
      const res = await axios.post(`${API}/parts/quote/pdf`, payload, {
        headers: { ...getAuthHeaders(), Accept: "application/pdf" },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${projectName.replace(/ /g, "_")}_Quote.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDeliveryCheckbox = (checked) => {
    setWantsDelivery(checked);
    if (checked) {
      setShowPostcodeModal(true);
    } else {
      setShowPostcodeModal(false);
      setDeliverySuccess("");
    }
  };

  const handleSendDeliveryRequest = async () => {
    if (!postcode.trim()) return;
    setSendingDelivery(true);
    setDeliverySuccess("");
    try {
      const payload = {
        project_name: projectName,
        postcode: postcode.trim(),
        items: basket.map((b) => ({ sku: b.sku, quantity: b.quantity })),
      };
      await axios.post(`${API}/parts/quote/request-delivery`, payload, {
        headers: getAuthHeaders(),
      });
      setDeliverySuccess("Delivery cost and lead time request sent! We'll get back to you shortly.");
      setShowPostcodeModal(false);
    } catch (err) {
      console.error("Delivery request failed:", err);
      setDeliverySuccess("Error sending request. Please try again.");
    } finally {
      setSendingDelivery(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="flex">
        {/* Main Content */}
        <div className={`flex-1 px-4 sm:px-6 lg:px-8 py-6 ${basket.length > 0 && !basketMinimised ? "sm:mr-[380px]" : ""}`}>
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="text-slate-600 hover:text-slate-900"
                data-testid="parts-lookup-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Dashboard
              </Button>
            </div>

            <div className="mb-6">
              <h1 className="text-2xl font-bold text-slate-900" data-testid="parts-lookup-title">
                Parts Lookup
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Search by product SKU to find pricing. Add items to your basket to build a quote.
              </p>
            </div>

            {/* Shipping Warning */}
            <div className="bg-orange-50 border border-orange-300 rounded-lg px-4 py-3 mb-6" data-testid="parts-lookup-shipping-warning">
              <p className="text-orange-800 font-semibold text-sm">
                Large and bulky items incur local shipping charges. Please speak to your ZKTeco representative for accurate costs.
              </p>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex gap-3 mb-6" data-testid="parts-lookup-search-form">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="parts-lookup-search-input"
                  placeholder="Type a product SKU..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-10 h-10"
                />
              </div>
              <Button type="submit" data-testid="parts-lookup-search-btn" className="h-10 px-6">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
              </Button>
            </form>

            {/* Results Count */}
            {searched && !loading && (
              <p className="text-sm text-slate-500 mb-4" data-testid="parts-lookup-count">
                {total} result{total !== 1 ? "s" : ""} found
                {query && <span> for "<strong>{query}</strong>"</span>}
              </p>
            )}

            {/* Results Table */}
            {results.length > 0 && (
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="parts-lookup-table">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        {visibleColumns.map((col) => (
                          <th
                            key={col}
                            className={`px-4 py-3 font-semibold text-slate-700 ${
                              col === "sku" ? "text-left" : "text-right"
                            }`}
                          >
                            {columnLabel(col)}
                          </th>
                        ))}
                        <th className="px-4 py-3 text-center font-semibold text-slate-700 w-20">
                          Add
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((row, idx) => (
                        <tr
                          key={row.sku}
                          className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}
                          data-testid={`parts-row-${row.sku}`}
                        >
                          {visibleColumns.map((col) => (
                            <td
                              key={col}
                              className={`px-4 py-2.5 ${
                                col === "sku"
                                  ? "text-left font-medium text-slate-900"
                                  : "text-right text-slate-700 tabular-nums"
                              }`}
                            >
                              {col === "sku" ? row[col] : formatPrice(row[col])}
                            </td>
                          ))}
                          <td className="px-4 py-2.5 text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => addToBasket(row)}
                              className="h-7 w-7 p-0"
                              data-testid={`add-to-basket-${row.sku}`}
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Empty State */}
            {searched && !loading && results.length === 0 && (
              <div className="text-center py-16 bg-white rounded-lg border border-slate-200" data-testid="parts-lookup-empty">
                <Search className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No parts found</p>
                <p className="text-slate-400 text-sm mt-1">Try a different SKU search term.</p>
              </div>
            )}
          </div>
        </div>

        {/* Basket Sidebar */}
        {basket.length > 0 && (
          basketMinimised ? (
            /* Minimised Basket Bar */
            <div
              className="fixed right-0 bottom-0 sm:top-0 sm:bottom-auto w-full sm:w-[380px] bg-white border-t sm:border-l border-slate-200 shadow-lg z-50 px-4 py-3 flex items-center justify-between cursor-pointer"
              onClick={() => setBasketMinimised(false)}
              data-testid="parts-basket-minimised"
            >
              <div className="flex items-center gap-3">
                <ShoppingCart className="h-5 w-5 text-slate-700" />
                <span className="font-bold text-slate-900">Basket</span>
                <span className="bg-slate-200 text-slate-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {basket.reduce((s, b) => s + b.quantity, 0)}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-bold text-slate-900">{formatPrice(basketTotal)}</span>
                <ChevronUp className="h-4 w-4 text-slate-500" />
              </div>
            </div>
          ) : (
          /* Full Basket Sidebar */
          <div className="fixed right-0 top-0 h-full w-full sm:w-[380px] bg-white border-l border-slate-200 shadow-lg flex flex-col z-50" data-testid="parts-basket-sidebar">
            {/* Sidebar Header */}
            <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-slate-700" />
                  <h2 className="font-bold text-slate-900">Basket</h2>
                  <span className="bg-slate-200 text-slate-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                    {basket.reduce((s, b) => s + b.quantity, 0)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-slate-700 h-7 w-7 p-0"
                    onClick={() => setBasketMinimised(true)}
                    data-testid="minimise-basket-btn"
                    title="Minimise basket"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-red-500 h-7 w-7 p-0"
                    onClick={() => { setBasket([]); setWantsDelivery(false); setDeliverySuccess(""); }}
                    data-testid="clear-basket-btn"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Basket Items */}
            <div className="flex-1 overflow-y-auto px-5 py-3">
              {basket.map((item) => (
                <div
                  key={item.sku}
                  className="flex items-start justify-between py-3 border-b border-slate-100 last:border-0"
                  data-testid={`basket-item-${item.sku}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {item.sku}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {formatPrice(getItemPrice(item))} each
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 ml-3">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 w-6 p-0"
                      onClick={() => updateQuantity(item.sku, item.quantity - 1)}
                      data-testid={`qty-minus-${item.sku}`}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateQuantity(item.sku, parseInt(e.target.value) || 1)}
                      className="h-6 w-10 text-center text-xs px-1"
                      data-testid={`qty-input-${item.sku}`}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 w-6 p-0"
                      onClick={() => updateQuantity(item.sku, item.quantity + 1)}
                      data-testid={`qty-plus-${item.sku}`}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                      onClick={() => removeFromBasket(item.sku)}
                      data-testid={`remove-item-${item.sku}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Basket Footer */}
            <div className="border-t border-slate-200 px-5 py-4 bg-slate-50">
              {/* Subtotal */}
              <div className="flex justify-between items-center text-sm text-slate-600">
                <span>Subtotal</span>
                <span className="tabular-nums">{formatPrice(basketSubtotal)}</span>
              </div>

              {/* Small Order Fee */}
              {smallOrderFee > 0 && (
                <div className="flex justify-between items-center text-sm text-orange-700 mt-1" data-testid="small-order-fee">
                  <span>{isDistributorTier ? "Small Order Fee (under £500)" : "Small Order Fee"}</span>
                  <span className="tabular-nums font-medium">{formatPrice(smallOrderFee)}</span>
                </div>
              )}

              {/* Total */}
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-200">
                <span className="text-sm font-semibold text-slate-700">Total</span>
                <span className="text-lg font-bold text-slate-900" data-testid="basket-total">
                  {formatPrice(basketTotal)}
                </span>
              </div>

              {/* Delivery Section */}
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={wantsDelivery}
                    onChange={(e) => handleDeliveryCheckbox(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    data-testid="delivery-checkbox"
                  />
                  <span className="text-xs text-blue-800 font-semibold">
                    Delivery Charges For Bulky Items Apply. Please check box to request lead time and accurate delivery costs.
                  </span>
                </label>
                {deliverySuccess && (
                  <p className="text-xs text-green-700 mt-2 flex items-center gap-1" data-testid="delivery-success">
                    <CheckCircle className="h-3 w-3" /> {deliverySuccess}
                  </p>
                )}
              </div>

              {/* Project Name */}
              <Input
                placeholder="Project name..."
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="mt-3 h-9 text-sm"
                data-testid="basket-project-name"
              />

              {/* Success Message */}
              {saveSuccess && (
                <p className="text-xs text-green-600 font-medium mt-2" data-testid="basket-save-success">
                  {saveSuccess}
                </p>
              )}

              {/* Actions */}
              <div className="flex gap-2 mt-3">
                <Button
                  onClick={handleSaveQuote}
                  disabled={saving}
                  className="flex-1 h-9 text-sm"
                  data-testid="basket-save-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                  Save Quote
                </Button>
                <Button
                  onClick={handleDownloadPdf}
                  disabled={downloading}
                  variant="outline"
                  className="flex-1 h-9 text-sm"
                  data-testid="basket-pdf-btn"
                >
                  {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
                  Download PDF
                </Button>
              </div>
            </div>
          </div>
          )
        )}

        {/* Postcode Modal */}
        {showPostcodeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" data-testid="postcode-modal-overlay">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4" data-testid="postcode-modal">
              <div className="flex items-center gap-2 mb-4">
                <Truck className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-bold text-slate-900">Delivery Cost and Lead Time</h3>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Enter your delivery postcode and we'll send you an accurate delivery cost and lead time.
              </p>
              <Input
                placeholder="e.g. SW1A 1AA"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                className="h-10 text-base font-medium tracking-wide mb-4"
                data-testid="postcode-input"
                autoFocus
              />
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 h-10"
                  onClick={() => { setShowPostcodeModal(false); setWantsDelivery(false); }}
                  data-testid="postcode-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 h-10 bg-green-600 hover:bg-green-700"
                  onClick={handleSendDeliveryRequest}
                  disabled={!postcode.trim() || sendingDelivery}
                  data-testid="postcode-go-btn"
                >
                  {sendingDelivery ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  Go
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
